<script setup lang="ts">
  import FlowPathInput from '@/components/form/FlowPathInput.vue'
  import {
    DropDown,
    FormFieldContainer,
    InputField,
    MULTIPLE_ENTRY_INPUT_TYPES,
    SwitchButton,
    type MultipleEntryElement,
    type MultipleEntryInputType,
  } from '@logistiek-zonder-papier/lzp-library'
  import { reactive } from 'vue'

  const field = defineModel<MultipleEntryElement>({ required: true })
  const touched = reactive({
    storeLocation: false,
  })

  const limits: {
    key: 'minEntries' | 'maxEntries' | 'maximumCharacterLimit'
    label: string
    placeholder: string
  }[] = [
    { key: 'minEntries', label: 'Minimum entries', placeholder: 'At least 1 makes it required' },
    { key: 'maxEntries', label: 'Maximum entries', placeholder: 'How many entries are allowed' },
    {
      key: 'maximumCharacterLimit',
      label: 'Maximum characters per entry',
      placeholder: 'How many characters are allowed',
    },
  ]

  function validateDto() {
    return touched.storeLocation &&
      field.value?.type === 'multipleEntry' &&
      !field.value?.storeLocation?.trim()
      ? 'Store Location is required'
      : null
  }
</script>

<template>
  <div class="field-config-container">
    <FormFieldContainer label="Label">
      <FlowPathInput
        placeholder="e.g. ${customer.name}"
        hint="Field label. Supports ${...} interpolation."
        v-model="field.label"
        :optional="true"
        type="text"
        :validate-paths="true"
      />
    </FormFieldContainer>

    <FormFieldContainer label="Placeholder">
      <FlowPathInput
        placeholder="Placeholder text"
        hint="Grey hint inside the input. Supports ${...} interpolation."
        v-model="field.placeholder"
        :optional="true"
        type="text"
        :validate-paths="true"
      />
    </FormFieldContainer>

    <FormFieldContainer label="Store Location" :error="validateDto()">
      <InputField
        placeholder="x.path.to.location"
        hint="Where the array of entries is stored."
        v-model="field.storeLocation"
        type="text"
        @blur="touched.storeLocation = true"
      />
    </FormFieldContainer>

    <DropDown
      :required="true"
      label="Input type"
      :modelValue="{ value: field.inputType }"
      idKey="value"
      :data="MULTIPLE_ENTRY_INPUT_TYPES.map(value => ({ value }))"
      @update:modelValue="
        (value: { value: MultipleEntryInputType }) => (field.inputType = value.value)
      "
    >
      <template #item="{ item }">{{ item.value }}</template>
    </DropDown>

    <template v-for="limit in limits" :key="limit.key">
      <FormFieldContainer :label="`Enable ${limit.label.toLowerCase()}`">
        <SwitchButton
          :modelValue="field[limit.key] !== null"
          @update:model-value="value => (field[limit.key] = value ? 0 : null)"
          size="md"
        />
      </FormFieldContainer>

      <FormFieldContainer v-if="field[limit.key] != null" :label="`${limit.label} (inclusive)`">
        <InputField :placeholder="limit.placeholder" v-model="field[limit.key]" type="number" />
      </FormFieldContainer>
    </template>

    <FormFieldContainer label="Duplicate message">
      <FlowPathInput
        placeholder="e.g. This value has already been added"
        hint="Shown under the input while the typed value is already in the list."
        v-model="field.duplicateMessage"
        :optional="true"
        :validate-paths="true"
      />
    </FormFieldContainer>

    <FormFieldContainer v-if="field.maxEntries != null" label="Maximum entries message">
      <FlowPathInput
        placeholder="e.g. No more entries can be added"
        hint="Shown under the input once the maximum number of entries is reached."
        v-model="field.maxEntriesMessage"
        :optional="true"
        :validate-paths="true"
      />
    </FormFieldContainer>

    <FormFieldContainer label="Error message">
      <FlowPathInput
        placeholder="Error message under the field"
        v-model="field.error"
        :optional="true"
        :validate-paths="true"
      />
    </FormFieldContainer>
  </div>
</template>

<style scoped>
  .field-config-container {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
</style>
